import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { environment } from '@environments/environment';
import { ApiResponse } from '@app/_models';
import { Inbox } from '@app/_components/inbox';
import { retry, catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { AlertService } from '@app/_services';
import { SharedService } from './../_shared/shared.service';
import { Msg } from '@app/_components/msg';

@Injectable({ providedIn: 'root' })
export class InboxService {

	statuses: any = [
		{ value: 1, name: 'Draft' },
		{ value: 2, name: 'Sent' },
		{ value: 3, name: 'Received' },
		{ value: 4, name: 'Failed' },
		{ value: 5, name: 'Outbox' }
	];

	msgContexts: any = [
		{ value: 0, name: 'Default' },
		{ value: 7, name: 'Reservations' },
		{ value: 8, name: 'Homeowners' },
		{ value: 9, name: 'BizDev' },
		{ value: 4, name: 'Developers' },
	];

	msgSubContextMap: { [contextId: number]: any[] } = {
		7: [ // Reservations
			{ value: 1001, name: 'Default' },
			{ value: 1002, name: 'Reservation Inquiry' },
			{ value: 1003, name: 'Reservation Edit' },
			{ value: 1004, name: 'Reservation Cancel' },
			{ value: 1005, name: 'Reservation Review' },
			{ value: 1006, name: 'Quote' },
			{ value: 1007, name: 'Rental Car' },
			{ value: 1008, name: 'Reservation Notification' },
			{ value: 1009, name: 'Reservation Hold Expire' },
			{ value: 1010, name: 'Reservation Hold Reminder' },
		],
		8: [ // Homeowners
			{ value: 2003, name: 'Homeowner Statement' },
			{ value: 2004, name: 'Homeowner Statement Mail' },
		],
		// Add more mappings as needed
	};

	msgRoles: any = [
		{ value: 0, name: 'DEFAULT' },
		{ value: 1, name: 'OWNER' },
		{ value: 2, name: 'GUEST' },
		{ value: 3, name: 'TRAVEL_AGT' },
		{ value: 4, name: 'EMPLOYEE' },
		{ value: 5, name: 'VENDOR' },
	];

	msgOrigin: any = [
		{ value: 0, name: '' },
		{ value: 1, name: 'Admin Communication Inbox' },
		{ value: 2, name: 'Sms' },
		{ value: 3, name: 'Call' },
		{ value: 4, name: 'Vrbo API' },
		{ value: 5, name: 'Guest Portal' },
		{ value: 6, name: 'Email' }
	];

	msgOriginFiltered: any = [
		{ value: 1, name: 'Admin Communication Inbox' },
		{ value: 2, name: 'Sms' },
		{ value: 3, name: 'Call' },
		{ value: 4, name: 'Vrbo API' },
		{ value: 5, name: 'Guest Portal' },
		{ value: 6, name: 'Email' }
	];

	constructor
		(
			private http: HttpClient,
			private alertService: AlertService,
			private sharedService: SharedService
		) { }

	urlEndpoint = 'msgs';
	reservationUrlEndpoint = 'reservations';

	add(inbox: Inbox): Observable<ApiResponse> {
		console.log('posting... ' + JSON.stringify(inbox));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, inbox).pipe();
	}

	sendEmailToGuest(payload: any): Observable<ApiResponse> {
		console.log('posting... ' + JSON.stringify(payload));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/order/reservation/notification/to-guest`, payload).pipe();
	}

	update(inbox: Inbox): Observable<ApiResponse> {
		return this.http.patch<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, inbox).pipe();
	}

	delete(developmentId: number): Observable<ApiResponse> {
		return this.http.delete<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${developmentId}`).pipe();
	}

	updateMsgContext(msg: Inbox): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/v2/update-context`, msg).pipe();
	}

	updateMsgSubContext(msg: Inbox): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/v2/update-sub-context`, msg).pipe();
	}

	updateMsgRole(msg: Inbox): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/v2/update-role`, msg).pipe();
	}

	findAll(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`).pipe();
	}

	findAllInboxes(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`).pipe();
	}

	findByFilter(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any): Observable<ApiResponse> {
		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
		console.log('posting... ' + JSON.stringify(filters));
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, { params: params }).pipe();

	}
  findByFilterForTopic(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any, topic: any, searchKeyWork: string, searchKeyFilter: string = 'email', view?: string): Observable<ApiResponse> {
    let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
    console.log('posting... ' + JSON.stringify(filters));
    let appender = '';
    if ( topic){
      appender = '?topic=' + topic;
      if (searchKeyWork){
        appender += '&searchKeyWork=' + searchKeyWork;
        appender += '&searchKeyFilter=' + searchKeyFilter;
      }
    }
    else if (searchKeyWork){
      appender = '?searchKeyWork=' + searchKeyWork;
      appender += '&searchKeyFilter=' + searchKeyFilter;
    }
    if (view) {
      appender += (appender ? '&' : '?') + 'view=' + view;
    }
    return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/inbox/${appender}`, { params: params }).pipe();

  }

  findGuestCommByFilter(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any, orderId: number): Observable<ApiResponse> {
    let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
    console.log('posting... ' + JSON.stringify(filters));
    return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/guest-comm/${orderId}`, { params: params }).pipe();
  }

  findGuestCommForRealtorByFilter(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any, realtorProfileId: number): Observable<ApiResponse> {
    let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
    console.log('posting... ' + JSON.stringify(filters));
    return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/guest-comm/realtor/${realtorProfileId}`, { params: params }).pipe();
  }


  findGuestConversation(
    fromEmail: string | null,
    toEmail: string | null,
    fromMobile: string | null,
    toMobile: string | null,
    page: number,
    size: number,
    msgConversationId?: number | null
  ): Observable<ApiResponse> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size));

    if (msgConversationId) { params = params.set('msgConversationId', String(msgConversationId)); }
    if (fromEmail)  { params = params.set('fromEmail', fromEmail); }
    if (toEmail)    { params = params.set('toEmail', toEmail); }
    if (fromMobile) { params = params.set('fromMobile', fromMobile); }
    if (toMobile)   { params = params.set('toMobile', toMobile); }

    return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/guest-conversion`, { params }).pipe();
  }

  getSmsDetail(msgId: number, msgRecipientId: number): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/sms-detail/${msgId}/${msgRecipientId}`).pipe();
  }

	refresh(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/imap_configurations/refresh`).pipe(retry(0));
	}

  sendOutboxMails(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/send-outbox-msges`).pipe(retry(0));
  }
	replyMail(msg: Msg): Observable<ApiResponse> {
		console.log('posting... ' + JSON.stringify(msg));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/replyMail`, msg).pipe();
	}
  replyMailWithAttachment(msg: any, files: any[]): Observable<ApiResponse> {
    let formData = new FormData();
    formData.append('form', new Blob([JSON.stringify(msg)], {type: 'application/json'}));
    files.forEach(file  => {
      formData.append('files', file);
    });
    console.log('posting... ' + JSON.stringify(msg));
    return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/replyMail`, msg).pipe();
  }

	sendMail(msg: any): Observable<ApiResponse> {
		console.log('posting... ' + JSON.stringify(msg));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/v2/reply-mail`, msg).pipe();
	}

	findInboxes(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any): Observable<ApiResponse> {
		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
		console.log('posting... ' + JSON.stringify(filters));
		return this.http.get<ApiResponse>(`${environment.apiUrl}/inboxes`, { params: params }).pipe();

	}

	findById(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}`).pipe();
	}


	findChildMails(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/child/${id}`).pipe();
	}

	getOrderIdByReservationId(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.reservationUrlEndpoint}/get-order-Id/${id}`).pipe();
	}

	audit(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}/audit`).pipe();
	}

	// getInboxNames(): Observable<ApiResponse> {
	// 	return this.http.get<ApiResponse>(`${environment.apiUrl}/imap_configurations/inbox_name`).pipe();
	// }


	prepareParamsFromPrimeNgDataTable(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any) {
		try {
			console.log(
				'  |  page:' + page
				+ '  |  size:' + size
				+ '  |  sortBy:' + sortBy
				+ '  |  sortOrder:' + sortOrder
				+ '  |  multiSortMeta:' + JSON.stringify(multiSortMeta)
				+ '  |  globalFilter:' + globalFilter
				+ '  |  filters:' + JSON.stringify(filters)
			);

			let params = new HttpParams();

			if (!isNaN(page)) { params = params.append('page', page); }
			if (!isNaN(size)) { params = params.append('size', size); }
			if (sortBy) { params = params.append('sort', sortBy + ',' + (sortOrder === 1 ? 'asc' : 'desc')); }
			if (multiSortMeta) {
				// if sortMode="multiple" in primeng table then multiSortMeta is used for ALL sorting (even just one column.)
				// Otherwise, single sorting is used above.
				// event.multiSortMeta:[{"field":"lastName","order":1},{"field":"firstName","order":1}]
				for (let i = 0; i < multiSortMeta.length; i++) {
					let json = JSON.parse(JSON.stringify(multiSortMeta[i]));
					params = params.append('sort', json.field + ',' + (json.order === 1 ? 'asc' : 'desc'));
				}
			}
			// globalFilter works, but did not implemnt in Spring but not hard to write custom findAll and specify certain columns to look.
			if (globalFilter) { params = params.append('filter', globalFilter); }

			// multiselect or input or whatever... these work slick.
			if (filters) {
				// key:status val:{"value":["A"],"matchMode":"in"}
				for (let i = 0, keys = Object.keys(filters), ii = keys.length; i < ii; i++) {
					console.log('key:' + keys[i] + ' val:' + JSON.stringify(filters[keys[i]]));
					params = params.append(keys[i], filters[keys[i]].value);
				}
			}
			return params;
		} catch (error) {
			console.log('prepareParamsFromPrimeNgDataTable()' + error);
		}
	}
  findByFilterGuestComm(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any): Observable<ApiResponse> {
    let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
    console.log('posting... ' + JSON.stringify(filters));
    return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/guest-comm`, { params: params }).pipe();

  }

  findConversation(from: string, to: string, page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta: any, globalFilter: any, filters: any): Observable<ApiResponse> {
    let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
    // Add from and to parameters for conversation
    params = params.append('from', from);
    params = params.append('to', to);
    console.log('Fetching conversation: from=' + from + ', to=' + to + ', page=' + page);
    return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/conversion`, { params: params }).pipe();
  }

  findEmailConversation(from: string, to: string): Observable<ApiResponse> {
    let params = this.sharedService.prepareParamsFromPrimeNgDataTable(0, 50, null, null, null, null, null);
    params = params.append('from', from);
    params = params.append('to', to);
    console.log('Fetching email conversation: from=' + from + ', to=' + to);
    return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/email-conversion`, { params: params }).pipe();
  }
}
